import {FC} from "react";
import {Box, Button, Typography} from "@mui/material";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {useApp2Auth} from "@/app2/auth";
import {loginButtonSx} from "@/app2/shared/styles/loginButtonSx";

/** Distance from viewport bottom to the mobile LOG IN button (thumb zone). */
const MOBILE_LOGIN_BUTTON_BOTTOM = "calc(clamp(122px, calc(16vh + 50px), 170px) + env(safe-area-inset-bottom, 0px))";

export const LandingPage: FC = () => {
	const mq = useMq();
	const {loginWithOAuth} = useApp2Auth();

	const handleLogin = async (): Promise<void> => {
		await loginWithOAuth();
	};

	return (
		<Box
			sx={{
				position: "relative",
				width: "100%",
				height: "100vh",
				"@supports (height: 100dvh)": {
					height: "100dvh"
				},
				backgroundImage: "url(/landing-bg.png)",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
				display: "flex",
				alignItems: mq.mobile ? "flex-end" : "center",
				justifyContent: "center",
				overflow: "hidden"
			}}
		>
			<Box
				sx={{
					position: mq.mobile ? "relative" : "absolute",
					zIndex: 2,
					width: mq.mobile ? "100%" : "552px",
					height: mq.mobile ? "auto" : "323px",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					padding: mq.mobile ? "40px 24px 32px 24px" : "0px",
					background: "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)",
					backdropFilter: "blur(3px)",
					WebkitBackdropFilter: "blur(3px)",
					borderRadius: mq.mobile ? "0" : "30px",
					...(mq.mobile ? {} : {
						left: "calc(50% - 552px/2)",
						top: "calc(50% - 323px/2 + 0.5px)"
					})
				}}
			>
				<Box
					sx={{
						width: mq.mobile ? "100%" : "440px",
						height: mq.mobile ? "auto" : "323px",
						padding: mq.mobile ? "40px 24px 0 24px" : "50px 24px",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: mq.mobile ? "flex-end" : "center",
						// Reserve space for the thumb-zone button on mobile
						paddingBottom: mq.mobile
							? `calc(42px + 24px + ${MOBILE_LOGIN_BUTTON_BOTTOM})`
							: undefined
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: mq.mobile ? "center" : "flex-start",
							alignItems: "center",
							gap: "12px",
							width: mq.mobile ? "100%" : "156px",
							maxWidth: mq.mobile ? "392px" : "none",
							height: "35px",
							opacity: 1,
							marginBottom: mq.mobile ? "16px" : "0px"
						}}
					>
						<img
							src="/logo192.png"
							alt="Improve Logo"
							style={{
								width: mq.mobile ? "26px" : "34px",
								height: mq.mobile ? "26px" : "34px",
								objectFit: "contain"
							}}
						/>
						<Typography
							sx={{
								color: "#FFFFFF",
								fontSize: mq.mobile ? "30px" : "28px",
								fontWeight: 700,
								fontFamily: "Titillium Web, sans-serif",
								lineHeight: 1
							}}
						>
							Improve
						</Typography>
					</Box>

					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							width: mq.mobile ? "100%" : "392px",
							height: mq.mobile ? "auto" : "96px",
							flex: "none",
							alignSelf: "center",
							flexGrow: 0,
							marginBottom: mq.mobile ? 0 : "28px"
						}}
					>
						<Typography
							component="h2"
							sx={{
								color: "#FFFFFF",
								fontFamily: "Titillium Web, sans-serif",
								fontWeight: 400,
								fontSize: mq.mobile ? "32px" : "34px",
								lineHeight: "123.5%",
								letterSpacing: "0.25px",
								textAlign: "center",
								width: "100%",
								margin: 0
							}}
						>
							Ready to get better today?
						</Typography>
					</Box>

					{!mq.mobile && (
						<Button
							variant="contained"
							onClick={() => void handleLogin()}
							sx={{
								...loginButtonSx,
								width: "392px",
								maxWidth: "392px"
							}}
						>
							LOG IN
						</Button>
					)}
				</Box>
			</Box>

			{mq.mobile && (
				<Button
					variant="contained"
					onClick={() => void handleLogin()}
					sx={{
						...loginButtonSx,
						position: "absolute",
						zIndex: 3,
						left: "24px",
						right: "24px",
						width: "auto",
						maxWidth: "392px",
						margin: "0 auto",
						display: "block",
						bottom: MOBILE_LOGIN_BUTTON_BOTTOM
					}}
				>
					LOG IN
				</Button>
			)}
		</Box>
	);
};
